import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import {
  AppTooltip,
  LineChartRecharts,
  useDashboardStore,
  ViewSwitcher,
  viewSwitchObj,
} from '@devd-client/devd/components';
import { useCopilotActiveUserCount } from '../../graphql';
import { useState } from 'react';
import { CopilotDataCard } from './CopilotDataCard';
import { CopilotLoadingComponent } from './CopilotLoadingComponent';
import { Link, useNavigate } from 'react-router-dom';
import { MdInfoOutline } from 'react-icons/md';
import { useTheme } from '@chakra-ui/react';

type Props = {
  branches: string;
  type: string;
};
export const CopilotActiveUsers = ({ branches, type }: Props) => {
  const { selectedTeam, selected } = useDashboardStore();
  const [viewToggle, setViewToggle] = useState('Week');
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const {
    data: copilotActiveUsersData,
    isFetching: copilotActiveUsersFetching,
  } = useCopilotActiveUserCount(
    selectedTeam.value,
    selected.startDate,
    selected.endDate,
    selected.sprintId,
    viewToggle.toUpperCase(),
    '',
    branches,
    type
  );

  if (copilotActiveUsersFetching)
    return (
      <CopilotLoadingComponent title="Code Assitance Active users" height />
    );

  return (
    <Flex
      flexDirection={'column'}
      mt={4}
      p={4}
      bg="white"
      borderRadius="md"
      border="1px solid #E0E0E0"
    >
      <Flex align={'center'} gap={1} mb={4}>
        <Text fontSize="lg" fontWeight="semibold" color={'text.secondary'}>
          {copilotActiveUsersData?.chartMetadata?.chartTitle}
        </Text>
        {copilotActiveUsersData?.chartMetadata?.info && (
          <AppTooltip
            content={
              <Text
                fontSize="sm"
                color="text.secondary"
                fontWeight="normal"
                fontFamily="heading"
              >
                {copilotActiveUsersData?.chartMetadata?.info}
              </Text>
            }
            placement="bottom"
            trigger="hover"
          >
            <MdInfoOutline size={16} color={colors.text.secondary} />
          </AppTooltip>
        )}
      </Flex>
      <Flex>
        {copilotActiveUsersData?.chartDataState !== 'NO_INTEGRATION' && (
          <Flex direction="column" mr={8} w="250px" justifyContent={'center'}>
            <CopilotDataCard
              title="Avg. Copilot Active Users"
              number={`${parseFloat(
                copilotActiveUsersData?.data?.avgCoPilotActiveUsers
              )?.toFixed(2)}`}
            />
            <CopilotDataCard
              title="Avg. Contributors"
              number={`${parseFloat(
                copilotActiveUsersData?.data?.avgContributors
              )?.toFixed(2)}`}
            />
          </Flex>
        )}

        <Box
          w={
            copilotActiveUsersData?.chartDataState !== 'NO_INTEGRATION'
              ? '80%'
              : '100%'
          }
        >
          <Box w="fit-content" ml={'auto'} mb={8}>
            {copilotActiveUsersData?.chartDataState === 'NOT_CONFIGURED' ||
            copilotActiveUsersData?.chartDataState === 'NO_INTEGRATION' ||
            copilotActiveUsersData?.chartDataState === 'ERROR' ? (
              <AppTooltip
                trigger="hover"
                content={
                  <Flex>
                    <Text
                      fontFamily="heading"
                      fontSize="sm"
                      color="text.primary"
                      mr={2}
                    >
                      {copilotActiveUsersData?.chartError?.message}
                    </Text>
                    {copilotActiveUsersData?.chartError?.link && (
                      <Link to={`${copilotActiveUsersData?.chartError?.link}`}>
                        <Text
                          color="primary"
                          fontFamily="heading"
                          fontSize="sm"
                          textDecoration={'underline'}
                        >
                          Configure
                        </Text>
                      </Link>
                    )}
                  </Flex>
                }
              >
                <Box>
                  <Button
                    size={'sm'}
                    color={'error'}
                    border="1px"
                    borderColor={'error'}
                    backgroundColor={'white'}
                    fontFamily="heading"
                  >
                    {'why no data?'}
                  </Button>
                </Box>
              </AppTooltip>
            ) : (
              <ViewSwitcher
                tabList={viewSwitchObj}
                currentTab={viewToggle}
                handleSwitch={setViewToggle}
                height="25px"
              />
            )}
          </Box>
          {copilotActiveUsersData?.chartDataState === 'NO_INTEGRATION' ? (
            <Box
              w="full"
              height="300px"
              display="flex"
              justifyContent={'center'}
              alignItems="center"
              position="relative"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <Image
                height="100%"
                src={copilotActiveUsersData?.chartMetadata?.zsImage || ''}
                style={{ position: 'relative' }}
              />
              {isHovered && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  backgroundColor="rgba(0, 0, 0, 0.4)"
                  borderRadius="4px"
                >
                  {copilotActiveUsersData?.chartMetadata?.source && (
                    <Button
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      onClick={() => navigate('/settings/integrations')}
                    >
                      Connect{' '}
                      {copilotActiveUsersData?.chartMetadata?.source ===
                      'ISSUE TRACKING'
                        ? 'Jira'
                        : copilotActiveUsersData?.chartMetadata?.source ===
                          'REPOSITORY'
                        ? 'Github'
                        : ''}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            copilotActiveUsersData &&
            !copilotActiveUsersFetching && (
              <Box flex="1" height="350px">
                <LineChartRecharts
                  data={copilotActiveUsersData?.data?.data}
                  keys={copilotActiveUsersData?.keys || []}
                  chartMetadata={copilotActiveUsersData?.chartMetadata}
                  showLabel={true}
                  selectedTeam={selectedTeam.value}
                  selected={selected}
                  showYLabel={true}
                  hideBtn={true}
                  showCustomLegend={true}
                />
              </Box>
            )
          )}
        </Box>
      </Flex>
    </Flex>
  );
};
